import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { useList, errMsg } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { FormDialog, buildPayload } from "@/components/ResourcePage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Eye, UserCheck, StickyNote, Power } from "lucide-react";

const AI_CATEGORIES = ["course", "fee", "policy", "teacher", "timetable", "holiday", "payment",
  "registration", "trial_class", "reschedule", "exam", "promotion", "general"];
const CATEGORY_OPTIONS = AI_CATEGORIES.map((c) => ({ value: c, label: c.replace(/_/g, " ") }));
const fmtDate = (s) => (s || "").slice(0, 10) || "—";

// ---------------------------------------------------------------- /admin/growth/ai-knowledge
const KNOWLEDGE_FIELDS = [
  { name: "title", label: "Title", required: true, full: true },
  { name: "category", label: "Category", type: "select", required: true, options: CATEGORY_OPTIONS, defaultValue: "general" },
  { name: "visibility", label: "Visibility", type: "select", defaultValue: "public", options: [
    { value: "public", label: "Public (AI can use for public answers)" },
    { value: "internal_only", label: "Internal only (never shown publicly)" }] },
  { name: "content", label: "Content", type: "textarea", required: true },
  { name: "effective_start_date", label: "Effective from", type: "date" },
  { name: "effective_end_date", label: "Effective until", type: "date" },
];

export function GrowthKnowledge() {
  const { data: items, loading, reload } = useList("/admin/ai/knowledge");
  const [editing, setEditing] = useState(null); // null | "new" | doc

  const toggleActive = async (k) => {
    try {
      await api.put(`/admin/ai/knowledge/${k.id}`, { values: { active_status: !k.active_status } });
      toast.success(k.active_status ? "Article deactivated" : "Article activated");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="growth-knowledge-page">
      <PageHeader title="AI knowledge base"
        description="Only ACTIVE + PUBLIC articles will ground future AI answers. Internal-only knowledge is never exposed to the public."
        actions={<Button onClick={() => setEditing("new")} data-testid="new-knowledge-button"><Plus className="h-4 w-4 mr-1" />New article</Button>} />
      {loading ? <Spinner full /> : !items?.length ? <EmptyState text="No knowledge articles yet — add your first one." /> : (
        <div className="space-y-3 stagger-in">
          {items.map((k) => (
            <div key={k.id} className="rounded-lg border bg-card p-4 flex flex-wrap items-center gap-3" data-testid={`knowledge-${k.id}`}>
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-sm">{k.title}</p>
                <p className="text-xs text-muted-foreground line-clamp-1">{k.content}</p>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{k.category}</span>
              <StatusBadge status={k.visibility} testId={`knowledge-visibility-${k.id}`} />
              <StatusBadge status={k.active_status ? "active" : "inactive"} testId={`knowledge-active-${k.id}`} />
              <span className="text-xs text-muted-foreground hidden md:block">{fmtDate(k.updated_at)}</span>
              <Button size="sm" variant="ghost" onClick={() => setEditing(k)} data-testid={`edit-knowledge-${k.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" onClick={() => toggleActive(k)} data-testid={`toggle-knowledge-${k.id}`}><Power className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}
      <FormDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing === "new" ? "New knowledge article" : "Edit knowledge article"}
        fields={KNOWLEDGE_FIELDS}
        initial={editing && editing !== "new"
          ? { ...Object.fromEntries(KNOWLEDGE_FIELDS.map((f) => [f.name, editing[f.name] ?? ""])), __key: editing.id }
          : { category: "general", visibility: "public", __key: "new" }}
        submitLabel={editing === "new" ? "Create article" : "Save changes"}
        onSubmit={async (v) => {
          const payload = buildPayload(KNOWLEDGE_FIELDS, v);
          if (editing === "new") {
            await api.post("/admin/ai/knowledge", { ...payload, active_status: true });
            toast.success("Knowledge article created");
          } else {
            await api.put(`/admin/ai/knowledge/${editing.id}`, { values: payload });
            toast.success("Knowledge article updated");
          }
          reload();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------- /admin/growth/ai-faqs
export function GrowthFaqs() {
  const { data: items, loading, reload } = useList("/admin/ai/faqs");
  const { data: courses } = useList("/public/courses");
  const [editing, setEditing] = useState(null);

  const FAQ_FIELDS = [
    { name: "question", label: "Question", required: true, full: true },
    { name: "answer", label: "Answer", type: "textarea", required: true },
    { name: "category", label: "Category", type: "select", required: true, options: CATEGORY_OPTIONS, defaultValue: "general" },
    { name: "related_course_id", label: "Related course (optional)", type: "select", options: [
      { value: "none", label: "None" },
      ...(courses || []).map((c) => ({ value: c.id, label: c.name }))] },
    { name: "display_order", label: "Display order", type: "number", int: true, defaultValue: 0 },
  ];

  const toggleActive = async (f) => {
    try {
      await api.put(`/admin/ai/faqs/${f.id}`, { values: { active_status: !f.active_status } });
      toast.success(f.active_status ? "FAQ deactivated" : "FAQ activated");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="growth-faqs-page">
      <PageHeader title="AI FAQs" description="Frequently asked questions used for public answers, ordered by display order."
        actions={<Button onClick={() => setEditing("new")} data-testid="new-faq-button"><Plus className="h-4 w-4 mr-1" />New FAQ</Button>} />
      {loading ? <Spinner full /> : !items?.length ? <EmptyState text="No FAQs yet." /> : (
        <div className="space-y-3 stagger-in">
          {items.map((f) => (
            <div key={f.id} className="rounded-lg border bg-card p-4 flex flex-wrap items-center gap-3" data-testid={`faq-${f.id}`}>
              <span className="font-display font-bold text-muted-foreground w-8 text-center">#{f.display_order}</span>
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-sm">{f.question}</p>
                <p className="text-xs text-muted-foreground line-clamp-1">{f.answer}</p>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{f.category}</span>
              <StatusBadge status={f.active_status ? "active" : "inactive"} testId={`faq-active-${f.id}`} />
              <Button size="sm" variant="ghost" onClick={() => setEditing(f)} data-testid={`edit-faq-${f.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" onClick={() => toggleActive(f)} data-testid={`toggle-faq-${f.id}`}><Power className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}
      <FormDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing === "new" ? "New FAQ" : "Edit FAQ"}
        fields={FAQ_FIELDS}
        initial={editing && editing !== "new"
          ? { ...Object.fromEntries(FAQ_FIELDS.map((f) => [f.name, editing[f.name] ?? ""])), related_course_id: editing.related_course_id || "none", __key: editing.id }
          : { category: "general", related_course_id: "none", display_order: 0, __key: "new" }}
        submitLabel={editing === "new" ? "Create FAQ" : "Save changes"}
        onSubmit={async (v) => {
          const payload = buildPayload(FAQ_FIELDS, v);
          payload.display_order = payload.display_order ?? 0;
          if (payload.related_course_id === "none") payload.related_course_id = null;
          if (editing === "new") {
            await api.post("/admin/ai/faqs", { ...payload, active_status: true });
            toast.success("FAQ created");
          } else {
            await api.put(`/admin/ai/faqs/${editing.id}`, { values: payload });
            toast.success("FAQ updated");
          }
          reload();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------- /admin/growth/ai-conversations
function MessagesDialog({ conversation, onOpenChange }) {
  const { data: messages, loading } = useList(
    conversation ? `/admin/ai/conversations/${conversation.id}/messages` : null, [conversation?.id]);
  return (
    <Dialog open={!!conversation} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="messages-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Conversation — {conversation?.parent_name || "Visitor"}</DialogTitle>
          <DialogDescription className="sr-only">Read-only message history</DialogDescription>
        </DialogHeader>
        {loading ? <Spinner /> : !messages?.length ? <EmptyState text="No messages." /> : (
          <div className="space-y-2.5">
            {messages.map((m) => (
              <div key={m.id} data-testid={`message-${m.id}`}
                className={`rounded-lg p-3 text-sm max-w-[85%] ${
                  m.sender_type === "user" ? "bg-muted" :
                  m.sender_type === "ai_agent" ? "bg-secondary/10 ml-auto" : "bg-[#E7F0FE] ml-auto"}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                  {m.sender_type.replace(/_/g, " ")} · {(m.created_at || "").slice(11, 16)}
                </p>
                {m.message}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function GrowthConversations() {
  const { data: items, loading } = useList("/admin/ai/conversations");
  const [viewing, setViewing] = useState(null);
  return (
    <div data-testid="growth-conversations-page">
      <PageHeader title="AI conversations" description="Read-only history of public chats — original messages cannot be edited." />
      {loading ? <Spinner full /> : !items?.length ? <EmptyState text="No conversations yet." /> : (
        <div className="space-y-3 stagger-in">
          {items.map((c) => (
            <div key={c.id} className="rounded-lg border bg-card p-4 flex flex-wrap items-center gap-3" data-testid={`conversation-${c.id}`}>
              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold text-sm">{c.parent_name || "Anonymous visitor"} {c.student_name && <span className="text-muted-foreground font-normal">· {c.student_name}</span>}</p>
                <p className="text-xs text-muted-foreground">{[c.phone, c.email].filter(Boolean).join(" · ") || "No contact info"}</p>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E7F0FE] text-[#1D4ED8]">{(c.source_channel || "website").replace(/_/g, " ")}</span>
              <StatusBadge status={c.enquiry_status} />
              <span className="text-xs text-muted-foreground">{c.message_count} msg</span>
              <span className="text-xs text-muted-foreground hidden md:block">{fmtDate(c.updated_at)}</span>
              <Button size="sm" variant="outline" onClick={() => setViewing(c)} data-testid={`view-conversation-${c.id}`}>
                <Eye className="h-3.5 w-3.5 mr-1" />View
              </Button>
            </div>
          ))}
        </div>
      )}
      <MessagesDialog conversation={viewing} onOpenChange={(o) => !o && setViewing(null)} />
    </div>
  );
}

// ---------------------------------------------------------------- /admin/growth/ai-escalations
const ESCALATION_STATUSES = ["open", "in_progress", "resolved", "closed"];

export function GrowthEscalations() {
  const [status, setStatus] = useState("open");
  const { data: items, loading, reload } = useList(
    `/admin/ai/escalations${status ? `?status=${status}` : ""}`, [status]);
  const [noteTarget, setNoteTarget] = useState(null);
  const { user } = useAuth();

  const update = async (id, values, msg) => {
    try {
      await api.put(`/admin/ai/escalations/${id}`, { values });
      toast.success(msg);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="growth-escalations-page">
      <PageHeader title="AI escalations" description="Conversations where the AI needs a human reply — work through them like an inbox." />
      <div className="flex gap-2 mb-5">
        {["", ...ESCALATION_STATUSES].map((s) => (
          <Button key={s || "all"} size="sm" variant={status === s ? "default" : "outline"}
            onClick={() => setStatus(s)} data-testid={`escalation-filter-${s || "all"}`}>
            {s ? s.replace(/_/g, " ") : "All"}
          </Button>
        ))}
      </div>
      {loading ? <Spinner full /> : !items?.length ? <EmptyState text="No escalations here — inbox zero!" /> : (
        <div className="space-y-3 stagger-in">
          {items.map((t) => (
            <div key={t.id} className="rounded-lg border bg-card p-4" data-testid={`escalation-${t.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge status={t.priority} testId={`escalation-priority-${t.id}`} />
                <p className="font-medium text-sm flex-1 min-w-[200px]">{t.reason}</p>
                <StatusBadge status={t.status} testId={`escalation-status-${t.id}`} />
                <span className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</span>
              </div>
              {t.admin_note && <p className="text-xs text-muted-foreground mt-2 border-l-2 border-secondary pl-2">Note: {t.admin_note}</p>}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <select data-testid={`escalation-status-select-${t.id}`} className="border rounded-md px-2 py-1.5 text-xs bg-card"
                  value={t.status} onChange={(e) => update(t.id, { status: e.target.value }, "Status updated")}>
                  {ESCALATION_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <Button size="sm" variant="outline" data-testid={`assign-escalation-${t.id}`}
                  onClick={() => update(t.id, { assigned_admin_id: user?.id || "me", assigned_admin_name: user?.full_name || user?.email || "Admin" }, "Assigned to you")}>
                  <UserCheck className="h-3.5 w-3.5 mr-1" />{t.assigned_admin_name ? `Assigned: ${t.assigned_admin_name}` : "Assign to me"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setNoteTarget(t)} data-testid={`note-escalation-${t.id}`}>
                  <StickyNote className="h-3.5 w-3.5 mr-1" />{t.admin_note ? "Edit note" : "Add note"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <FormDialog
        open={!!noteTarget}
        onOpenChange={(o) => !o && setNoteTarget(null)}
        title="Admin note"
        fields={[{ name: "admin_note", label: "Note", type: "textarea", required: true }]}
        initial={{ admin_note: noteTarget?.admin_note || "", __key: noteTarget?.id }}
        submitLabel="Save note"
        onSubmit={async (v) => {
          await api.put(`/admin/ai/escalations/${noteTarget.id}`, { values: { admin_note: v.admin_note } });
          toast.success("Note saved");
          reload();
        }}
      />
    </div>
  );
}
